import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Package, Plus, Barcode, ArrowLeftRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ScanButton from '../../components/inventory/ScanButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Inventory = () => {
  const [truckId, setTruckId] = useState('TRUCK-001');
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [itemName, setItemName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('pcs');
  const [loading, setLoading] = useState(false);

  // Transfer form
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferForm, setTransferForm] = useState({ from_location: 'TRUCK-001', to_location: 'SHOP', item_name: '', quantity: '', unit: 'pcs', notes: '' });

  useEffect(() => {
    fetchInventory();
    fetchRequests();
    fetchTransfers();
  }, [truckId]);

  const fetchInventory = async () => {
    try {
      const { data } = await axios.get(`${API}/inventory/${truckId}`, { withCredentials: true });
      setItems(data);
    } catch (error) { console.error('Error fetching inventory:', error); }
  };

  const fetchRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/inventory/requests`, { withCredentials: true });
      setRequests(data);
    } catch (error) { console.error(error); }
  };

  const fetchTransfers = async () => {
    try {
      const { data } = await axios.get(`${API}/inventory/transfers`, { withCredentials: true });
      setTransfers(data);
    } catch (error) { console.error(error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inventory/request`, { truck_id: truckId, item_name: itemName, quantity_requested: parseInt(quantity), unit }, { withCredentials: true });
      toast.success('Inventory request submitted');
      setShowForm(false);
      setItemName(''); setQuantity(''); setUnit('pcs');
      await fetchRequests();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inventory/transfer`, {
        ...transferForm,
        quantity: parseInt(transferForm.quantity),
      }, { withCredentials: true });
      toast.success('Transfer request submitted');
      setShowTransfer(false);
      setTransferForm({ from_location: 'TRUCK-001', to_location: 'SHOP', item_name: '', quantity: '', unit: 'pcs', notes: '' });
      fetchTransfers();
    } catch (err) { toast.error('Failed'); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'fulfilled': case 'completed': case 'approved': return 'text-green-500';
      case 'rejected': return 'text-red-500';
      default: return 'text-[#D4AF37]';
    }
  };

  const lowStock = items.filter(i => (i.min_quantity || 0) > 0 && (i.quantity || 0) <= (i.min_quantity || 0));

  return (
    <div data-testid="inventory-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Inventory</h2>
          <p className="text-[#A3A3A3]">Manage your truck stock</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="show-transfer-btn" onClick={() => setShowTransfer(!showTransfer)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><ArrowLeftRight className="w-4 h-4 mr-2" /> Transfer</Button>
          <Button data-testid="request-inventory-button" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> Request</Button>
        </div>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
        <Label htmlFor="truck-select" className="text-white mb-2 block">Select Truck</Label>
        <Select value={truckId} onValueChange={setTruckId}>
          <SelectTrigger id="truck-select" className="bg-[#0A0A0A] border-[#262626] text-white w-64"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="TRUCK-001" className="text-white">TRUCK-001</SelectItem>
            <SelectItem value="TRUCK-002" className="text-white">TRUCK-002</SelectItem>
            <SelectItem value="TRUCK-003" className="text-white">TRUCK-003</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div data-testid="low-stock-banner-employee" className="bg-[#1a0e0e] border border-red-700/40 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <p className="text-red-400 font-bold uppercase tracking-wider text-xs">Low Stock — Time to Restock</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStock.map((i) => (
              <span key={i.id || `${i.item_name}-${i.unit}`} className="bg-[#262626] text-white text-xs px-3 py-1 rounded-full">{i.item_name}: <span className="text-red-400 font-bold">{i.quantity}</span>/{i.min_quantity} {i.unit}</span>
            ))}
          </div>
        </div>
      )}

      {/* Barcode Scanner — uses the SAME shared scanner as the Inventory Scanner hub
          (V2 registry lookup + stock actions). Replaces the old dead V1 scan field. */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
        <h3 className="font-['Outfit'] text-lg font-bold text-white mb-1 flex items-center gap-2"><Barcode className="w-5 h-5 text-[#D4AF37]" /> Barcode Scan</h3>
        <p className="text-[#A3A3A3] text-sm mb-3">Scan a label to look up an item and record use, restock, transfer, count or a restock request.</p>
        <div className="flex flex-wrap gap-2">

          <ScanButton mode="lookup" label="Look Up Item" testId="inventory-scan-lookup" onDone={fetchInventory} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]" />
        </div>
      </div>

      {/* Transfer Form */}
      {showTransfer && (
        <div className="bg-[#141414] border border-[#D4AF37] rounded-md p-6">
          <h3 className="font-['Outfit'] text-lg font-bold text-white mb-3 flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-[#D4AF37]" /> Van Transfer Request</h3>
          <form onSubmit={handleTransfer} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-white text-xs">From</Label>
              <Select value={transferForm.from_location} onValueChange={(v) => setTransferForm({...transferForm, from_location: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {['TRUCK-001','TRUCK-002','TRUCK-003','SHOP'].map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-white text-xs">To</Label>
              <Select value={transferForm.to_location} onValueChange={(v) => setTransferForm({...transferForm, to_location: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {['TRUCK-001','TRUCK-002','TRUCK-003','SHOP'].map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-white text-xs">Item</Label>
              <Input data-testid="transfer-item" value={transferForm.item_name} onChange={(e) => setTransferForm({...transferForm, item_name: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white text-xs">Quantity</Label>
              <Input data-testid="transfer-qty" type="number" value={transferForm.quantity} onChange={(e) => setTransferForm({...transferForm, quantity: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white text-xs">Notes</Label>
              <Input value={transferForm.notes} onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <Button data-testid="transfer-submit" type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] self-end">Submit Transfer</Button>
          </form>
        </div>
      )}

      {showForm && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="item-name" className="text-white">Item Name</Label>
                <Input data-testid="inventory-item-name" id="item-name" type="text" value={itemName} onChange={(e) => setItemName(e.target.value)} required className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantity" className="text-white">Quantity</Label>
                <Input data-testid="inventory-quantity" id="quantity" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} required className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit" className="text-white">Unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger id="unit" className="bg-[#0A0A0A] border-[#262626] text-white w-full"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="pcs" className="text-white">Pieces</SelectItem>
                  <SelectItem value="ft" className="text-white">Feet</SelectItem>
                  <SelectItem value="rolls" className="text-white">Rolls</SelectItem>
                  <SelectItem value="boxes" className="text-white">Boxes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button data-testid="inventory-submit-button" type="submit" disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Submit Request</Button>
              <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div>
        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4">Current Inventory - {truckId}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.length === 0 ? (
            <div className="col-span-3 bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No items in inventory</div>
          ) : items.map((item) => {
            const isLow = (item.min_quantity || 0) > 0 && (item.quantity || 0) <= (item.min_quantity || 0);
            return (
              <div key={item.id || `${item.item_name}-${item.barcode || ''}`} className={`bg-[#141414] border ${isLow ? 'border-red-700/50' : 'border-[#262626]'} rounded-md p-6`}>
                <div className="flex items-start gap-3">
                  <Package className={`w-5 h-5 mt-1 ${isLow ? 'text-red-400' : 'text-[#D4AF37]'}`} />
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1">{item.item_name}</h4>
                    <p className={`text-lg font-bold ${isLow ? 'text-red-400' : 'text-[#D4AF37]'}`}>{item.quantity} {item.unit}</p>
                    {item.barcode && <p className="text-[#525252] text-xs font-mono mt-1">#{item.barcode}</p>}
                    {item.min_quantity > 0 && <p className="text-[#525252] text-xs">Min: {item.min_quantity}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4">My Transfers</h3>
        <div className="grid gap-3">
          {transfers.length === 0 ? (
            <div className="bg-[#141414] border border-[#262626] rounded-md p-6 text-center text-[#A3A3A3] text-sm">No transfers yet</div>
          ) : transfers.map((t) => (
            <div key={t.id} className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-white text-sm">{t.item_name} — {t.quantity} {t.unit}</p>
                <p className="text-[#A3A3A3] text-xs">{t.from_location} → {t.to_location}</p>
              </div>
              <span className={`text-xs uppercase font-medium ${getStatusColor(t.status)} bg-[#262626] px-3 py-1 rounded-md`}>{t.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-['Outfit'] text-xl font-bold text-white mb-4">My Requests</h3>
        <div className="grid gap-4">
          {requests.length === 0 ? (
            <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No requests yet</div>
          ) : requests.map((request) => (
            <div key={request.id || `${request.item_name}-${request.truck_id}`} className="bg-[#141414] border border-[#262626] rounded-md p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Package className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white font-medium">{request.item_name} - {request.quantity_requested} {request.unit}</span>
                  </div>
                  <p className="text-[#A3A3A3] ml-8 text-sm">Truck: {request.truck_id}</p>
                </div>
                <div className={`px-3 py-1 rounded-md text-sm font-medium ${getStatusColor(request.status)} bg-[#262626]`}>{request.status.toUpperCase()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Inventory;
